import { Extension, Node, mergeAttributes, type CommandProps } from '@tiptap/core';
import { OrderedList } from '@tiptap/extension-list';
import { Fragment, Slice, type Node as PMNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { ESTILO_PADRAO } from '../estilos';

/* ===================== Numeração jurídica ===================== */

export type TipoNumeracao = 'romano' | 'decimal' | 'alfa' | 'multinivel';

const LIST_STYLE: Record<TipoNumeracao, string> = {
  romano: 'upper-roman',
  decimal: 'decimal',
  alfa: 'lower-alpha',
  multinivel: 'decimal',
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    numeracaoJuridica: {
      /** Numera os parágrafos da seleção: I, II · 1, 2 · a), b) · 1.1. */
      numerar: (tipo: TipoNumeracao) => ReturnType;
      desnumerar: () => ReturnType;
    };
    notaRodape: {
      /** Insere a chamada no cursor; o texto da nota fica no pé da página. */
      inserirNota: (texto?: string) => ReturnType;
      definirTextoNota: (pos: number, texto: string) => ReturnType;
    };
    quebra: {
      inserirQuebra: (tipo: TipoQuebra) => ReturnType;
    };
  }
}

/**
 * Lista numerada com o tipo jurídico no atributo. O `list-style-type` vai
 * em linha (Word e PDF leem); o "a)" com parêntese e o "1.1" são do CSS do
 * editor, que o `data-tipo` aciona.
 */
export const NumeracaoJuridica = OrderedList.extend({
  name: 'orderedList',
  addAttributes() {
    return {
      ...this.parent?.(),
      tipo: {
        default: 'decimal' as TipoNumeracao,
        parseHTML: (el: HTMLElement) => (el.getAttribute('data-tipo') as TipoNumeracao) || 'decimal',
        renderHTML: (attrs: { tipo: TipoNumeracao }) => ({
          'data-tipo': attrs.tipo,
          style: `list-style-type:${LIST_STYLE[attrs.tipo] ?? 'decimal'}`,
        }),
      },
    };
  },
  addCommands() {
    return {
      ...this.parent?.(),
      numerar:
        (tipo: TipoNumeracao) =>
        ({ editor, chain }: CommandProps) => {
          if (!editor.isActive('orderedList')) chain().toggleOrderedList().run();
          return chain().updateAttributes('orderedList', { tipo }).run();
        },
      desnumerar:
        () =>
        ({ editor, chain }: CommandProps) =>
          editor.isActive('orderedList') ? chain().toggleOrderedList().run() : false,
    };
  },
});

/* ===================== Nota de rodapé ===================== */

const CHAVE_NOTAS = new PluginKey('notasRodape');

/**
 * A chamada é um átomo no texto (`<sup data-nota="…">3</sup>`) que carrega o
 * texto da nota. Guardar a nota DENTRO da chamada faz recortar e colar levar
 * a nota junto, e apagar a chamada apagar a nota — como no Word.
 *
 * O número é refeito a cada mudança, na ordem do documento: inserir uma nota
 * no começo renumera todas as de depois.
 */
export const NotaRodape = Node.create({
  name: 'nota',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      texto: { default: '', parseHTML: (el) => el.getAttribute('data-nota') ?? '' },
      numero: { default: 1, parseHTML: (el) => Number(el.textContent) || 1, renderHTML: () => ({}) },
    };
  },
  parseHTML() {
    // Prioridade acima do Sobrescrito, que também lê <sup>.
    return [{ tag: 'sup[data-nota]', priority: 100 }];
  },
  renderHTML({ node, HTMLAttributes }) {
    return ['sup', mergeAttributes(HTMLAttributes, { class: 'cm-ef-nota-ref', 'data-nota': node.attrs.texto }), String(node.attrs.numero)];
  },
  renderText({ node }) {
    return `[${node.attrs.numero}]`;
  },

  addCommands() {
    return {
      inserirNota:
        (texto = '') =>
        ({ chain }: CommandProps) =>
          chain().insertContent({ type: this.name, attrs: { texto } }).run(),
      definirTextoNota:
        (pos: number, texto: string) =>
        ({ tr, state, dispatch }: CommandProps) => {
          const node = state.doc.nodeAt(pos);
          if (!node || node.type.name !== this.name) return false;
          if (dispatch) dispatch(tr.setNodeMarkup(pos, undefined, { ...node.attrs, texto }));
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    const tipo = this.name;
    return [
      new Plugin({
        key: CHAVE_NOTAS,
        appendTransaction: (trs, _velho, novo) => {
          if (!trs.some((t) => t.docChanged)) return null;
          const tr = novo.tr;
          let n = 0;
          novo.doc.descendants((node, pos) => {
            if (node.type.name !== tipo) return;
            n += 1;
            if (node.attrs.numero !== n) tr.setNodeMarkup(pos, undefined, { ...node.attrs, numero: n });
          });
          return tr.docChanged ? tr : null;
        },
      }),
    ];
  },
});

/* ===================== Quebras ===================== */

export type TipoQuebra = 'pagina' | 'coluna' | 'secao' | 'secaoContinua';

/**
 * Quebra de página, de coluna ou de seção. A de página leva
 * `break-after: page` em linha: a impressão e o PDF respeitam.
 * O desenho na tela (fim de folha, rodapé, próxima folha) é do
 * `QuebraPaginaView`, injetado pelo editor.
 */
export const Quebra = Node.create({
  name: 'quebra',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return { tipo: { default: 'pagina' as TipoQuebra, parseHTML: (el) => (el.getAttribute('data-quebra') as TipoQuebra) || 'pagina' } };
  },
  parseHTML() {
    return [{ tag: 'div[data-quebra]' }];
  },
  renderHTML({ node }) {
    const tipo = node.attrs.tipo as TipoQuebra;
    const css = tipo === 'coluna' ? 'break-after:column' : tipo === 'secaoContinua' ? '' : 'break-after:page;page-break-after:always';
    return ['div', { 'data-quebra': tipo, class: 'cm-ef-quebra', style: css }];
  },
  addCommands() {
    return {
      inserirQuebra:
        (tipo: TipoQuebra) =>
        ({ chain }: CommandProps) =>
          chain().insertContent([{ type: this.name, attrs: { tipo } }, { type: 'paragraph' }]).run(),
    };
  },
});

/* ===================== HTML da IA ===================== */

/**
 * O HTML que a IA devolve (h1, h2, section) nos estilos da peça. O editor guarda
 * o estilo no parágrafo (`data-estilo`) e lê h1/h2 como corpo de texto: os títulos
 * das partes entravam sem negrito e sem espaço. h1 → Endereçamento, h2/h3/h4 →
 * Título de seção, `section` desembrulhada. HTML que já vem no formato da casa
 * (`data-estilo`) entra como está. `assinatura: false` não centraliza a
 * assinatura — para texto que não veio da IA (o do editor antigo), em que um
 * parágrafo do fim que cita a OAB pode ser corpo de texto.
 */
export function htmlDaIANosEstilos(html: string, { assinatura = true }: { assinatura?: boolean } = {}): string {
  const gatilho = assinatura ? /<(h[1-4]|section)\b|OAB/i : /<(h[1-4]|section)\b/i;
  if (!html || /data-estilo=/.test(html) || !gatilho.test(html) || typeof DOMParser === 'undefined') return html;
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  // Assinatura (nome e OAB, no fim): centralizada, sem o recuo de primeira linha
  // do Corpo — a segunda linha, depois do <br>, ficava na margem.
  const finais = assinatura ? Array.from(doc.body.children).filter((el) => el.tagName === 'P').slice(-3) : [];
  // `innerHTML`: no `textContent` o nome cola na OAB ("ARAUJOOAB") sem o <br>.
  const linhas = (p: Element) => p.innerHTML.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]*>/g, '');
  finais.filter((p) => { const t = linhas(p); return t.length < 200 && /\bOAB\b/.test(t); }).forEach((p) => {
    p.setAttribute('data-estilo', 'corpo');
    p.setAttribute('data-ajustes', JSON.stringify({ alinhamento: 'center', especial: 'nenhum', medida: 0 }));
  });
  doc.querySelectorAll('section').forEach((el) => el.replaceWith(...Array.from(el.childNodes)));
  doc.querySelectorAll('h1, h2, h3, h4').forEach((el) => {
    const p = doc.createElement('p');
    p.setAttribute('data-estilo', el.tagName === 'H1' ? 'enderecamento' : 'titulo');
    // O alinhamento que o texto do editor antigo trazia (`htmlLegadoParaForense`).
    const ajustes = el.getAttribute('data-ajustes');
    if (ajustes) p.setAttribute('data-ajustes', ajustes);
    p.append(...Array.from(el.childNodes));
    el.replaceWith(p);
  });
  return doc.body.innerHTML;
}

/* ===================== Colagem segura ===================== */

const TAGS_MANTIDAS = new Set(['P', 'BR', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'SUP', 'SUB', 'UL', 'OL', 'LI', 'TABLE', 'THEAD', 'TBODY', 'TR', 'TD', 'TH', 'A', 'DIV']);

/** Limpa o HTML que veio de fora: fica a estrutura e a ênfase, sai a diagramação. */
export function limparHtmlColado(html: string): string {
  // Copiado do próprio editor: já vem no formato da casa, entra como está.
  if (/data-estilo=/.test(html)) return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('style, script, meta, link, title, xml, o\\:p').forEach((el) => el.remove());
  const limpar = (el: Element) => {
    Array.from(el.children).forEach(limpar);
    if (!TAGS_MANTIDAS.has(el.tagName)) {
      // Span do Word com negrito/itálico só no estilo: vira a tag certa.
      const st = (el.getAttribute('style') ?? '').toLowerCase();
      let substituto: Element | null = null;
      if (/font-weight:\s*(bold|[6-9]00)/.test(st)) substituto = doc.createElement('strong');
      else if (/font-style:\s*italic/.test(st)) substituto = doc.createElement('em');
      if (substituto) { substituto.append(...Array.from(el.childNodes)); el.replaceWith(substituto); }
      else el.replaceWith(...Array.from(el.childNodes));
      return;
    }
    Array.from(el.attributes).forEach((a) => {
      if (!(el.tagName === 'A' && a.name === 'href') && !(/^(TD|TH)$/.test(el.tagName) && /^(colspan|rowspan)$/.test(a.name))) el.removeAttribute(a.name);
    });
  };
  Array.from(doc.body.children).forEach(limpar);
  return doc.body.innerHTML;
}

/**
 * O texto colado assume o estilo do parágrafo onde caiu. Negrito, itálico,
 * sublinhado, listas e tabelas vêm junto; fonte, tamanho, cor e margens de
 * fora ficam. Ctrl+Shift+V cola sem formatação nenhuma.
 */
export const ColagemSegura = Extension.create({
  name: 'colagemSegura',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('colagemSegura'),
        props: {
          transformPastedHTML: limparHtmlColado,
          transformPasted: (slice, view) => {
            const { $from } = view.state.selection;
            let estilo: string | null = null;
            for (let d = $from.depth; d >= 0; d--) if ($from.node(d).type.name === 'paragraph') { estilo = $from.node(d).attrs.estilo; break; }
            const alvo = estilo === 'titulo' || estilo === 'legenda' || estilo === 'enderecamento' ? ESTILO_PADRAO : estilo;
            const mapear = (frag: Fragment): Fragment => {
              const nos: PMNode[] = [];
              frag.forEach((n) => {
                if (n.isText) { nos.push(n); return; }
                const filhos = mapear(n.content);
                nos.push(
                  n.type.name === 'paragraph' && !n.attrs.estilo
                    ? n.type.create({ ...n.attrs, estilo: alvo }, filhos, n.marks)
                    : n.copy(filhos),
                );
              });
              return Fragment.fromArray(nos);
            };
            return new Slice(mapear(slice.content), slice.openStart, slice.openEnd);
          },
        },
      }),
    ];
  },
});
